<script setup>
/**
 * Página de Login - Single Sign-On (SSO)
 *
 * Esta página apenas redireciona para o WSO2 Identity Server.
 * NÃO há formulário de login local.
 *
 * Fluxo SSO:
 * 1. Usuário clica em "Entrar"
 * 2. É redirecionado para WSO2 (identidade.senai.br)
 * 3. Se já logado em outro sistema SENAI → Login automático (sem digitar credenciais)
 * 4. Se não logado → Apresenta tela de login do WSO2
 * 5. Após autenticação → Retorna para /auth/callback
 * 6. Backend cria sessão e redireciona para Home
 */
import Button from "primevue/button";

function handleLogin() {
  const apiUrl = `${import.meta.env.VITE_HTTP_PROTOCOL}://${
    import.meta.env.VITE_API_URL
  }`;
  // Redireciona para o endpoint que inicia o fluxo SSO
  window.location.href = `${apiUrl}/auth/login`;
}
</script>

<template>
  <div class="login-container">
    <div class="background-animation">
      <div id="bg-gradient-1"></div>
      <div id="bg-gradient-2"></div>
      <div id="bg-gradient-3"></div>
    </div>
    <div class="login-card">
      <div class="login-header">
        <img
          src="@/assets/images/logoSenai.svg"
          alt="Logo Senai"
          class="login-logo"
        />
        <h1 class="welcome-text">Seja bem-vindo</h1>
      </div>
      <p class="login-description">
        Use suas credenciais SENAI para acessar o gerador de itens.
      </p>
      <Button
        label="Entrar"
        icon="pi pi-sign-in"
        @click="handleLogin"
        class="login-button"
        size="large"
      />
    </div>
  </div>
</template>

<style scoped>
.welcome-text {
  font-size: 1.4rem;
  font-weight: 700;
  margin-top: 16px;
  color: #111827;
  letter-spacing: -0.02em;
}

.login-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  position: relative;
  overflow: hidden;
}

.background-animation {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  pointer-events: none;
}

.background-animation div {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  animation-name: fade;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
  animation-duration: 16s;
}

@keyframes fade {
  0% {
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  40% {
    opacity: 1;
  }
  60% {
    opacity: 0;
  }
  80% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

#bg-gradient-1 {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%);
}

#bg-gradient-2 {
  background: linear-gradient(135deg, #fef7f0 0%, #fed7aa 50%, #f97316 100%);
  animation-delay: -4s;
}

#bg-gradient-3 {
  background: linear-gradient(135deg, #f0f9ff 0%, #bae6fd 50%, #0ea5e9 100%);
  animation-delay: -8s;
}

.login-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 48px 40px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  max-width: 440px;
  width: 100%;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
}

.login-header {
  margin-bottom: 24px;
}

.login-icon {
  font-size: 3rem;
  background: linear-gradient(135deg, #f5631a 0%, #e84910 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 16px;
}

h1 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.02em;
}

.login-description {
  color: #6b7280;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0 0 32px 0;
}

.login-button {
  width: 100%;
  padding: 12px 24px;
  font-size: 1.1rem;
  font-weight: 500;
  background: linear-gradient(135deg, #f5631a 0%, #e84910 100%);
  border: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.login-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(245, 99, 26, 0.4);
}

.login-button:active {
  transform: translateY(0);
}

@media (max-width: 480px) {
  .login-card {
    padding: 32px 24px;
  }

  h1 {
    font-size: 1.5rem;
  }

  .login-description {
    font-size: 0.95rem;
  }
}
</style>
